import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, object=logged-in
  const [favIds, setFavIds] = useState(new Set());

  const refreshFavIds = useCallback(async () => {
    try {
      const { data } = await api.get("/favorites/ids");
      setFavIds(new Set(data));
    } catch {
      setFavIds(new Set());
    }
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      refreshFavIds();
    } catch {
      setUser(false);
    }
  }, [refreshFavIds]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.token) localStorage.setItem("wv_token", data.token);
    setUser(data);
    refreshFavIds();
    return data;
  };

  const register = async (name, email, password) => {
    let ref = null;
    try {
      const p = new URLSearchParams(window.location.search);
      ref = p.get("ref") || localStorage.getItem("wv_ref") || null;
      if (p.get("ref")) localStorage.setItem("wv_ref", p.get("ref"));
    } catch {}
    const { data } = await api.post("/auth/register", { name, email, password, ref });
    if (data?.token) localStorage.setItem("wv_token", data.token);
    setUser(data);
    refreshFavIds();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("wv_token");
    setUser(false);
    setFavIds(new Set());
  };

  const toggleFavorite = async (listingId) => {
    const { data } = await api.post(`/favorites/${listingId}`);
    setFavIds((s) => {
      const n = new Set(s);
      if (data.favorited) n.add(listingId); else n.delete(listingId);
      return n;
    });
    return data.favorited;
  };

  return (
    <AuthContext.Provider
      value={{ user, login, register, logout, favIds, toggleFavorite, refreshFavIds, formatApiErrorDetail }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
